import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getStatusMessage } from './statusMessages';
import { API_ENDPOINTS } from './apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Signup.css';
import { useAuth } from "./AuthContext";

function Signup() {
  const navigate = useNavigate();
  const symbols = ['💖', '🌸', '💗', '💞', '🌼', '💑', '💍'];
  const { login, token } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [animateArrow, setAnimateArrow] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (token && token.trim() !== '') {
      navigate('/search');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (showTermsPopup) {
      setTimeout(() => {
        const bodyEl = document.querySelector('.modal-body');
        if (bodyEl && bodyEl.scrollHeight <= bodyEl.clientHeight) {
          setScrolledToBottom(true);
        }
      }, 100);
    }
  }, [showTermsPopup]);

  const validateEmail = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return 'Email is required';
    if (trimmed.length > 100) return 'Email too long';
    if (!/^[^\x00-\x1F\x7F\\"]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Invalid email';
    return '';
  };

  const validatePassword = (val) => {
    const trimmed = val.trim();
    if (trimmed.length < 8 || trimmed.length > 12) return 'Password must be 8–12 characters';
    if (!/^[\x20-\x7E]*$/.test(trimmed)) return 'Invalid characters in password';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimateArrow(true);
    setErrors([]);
    setMessage('');
    setLoading(true);

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError || passwordError || !acceptedTerms) {
      setTimeout(() => setAnimateArrow(false), 800);
      const termsError = !acceptedTerms ? 'You must agree to the Terms & Conditions' : '';
      setErrors([emailError, passwordError, termsError].filter(Boolean));
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post(API_ENDPOINTS.SIGNUP, { email, password });
      const { result, user, otp, errors: serverErrors } = res.data;

      setTimeout(() => setAnimateArrow(false), 800);

      if (result === 1) {
        if (otp === 6) {
          setErrors([getStatusMessage(otp)]);
          setLoading(false);
          return;
        }
        const otpMsg = getStatusMessage(otp);
        setMessage(otpMsg.trim());

        setTimeout(() => {
          navigate('/VerifyOTP', { state: { email, rotp: otp } });
        }, 100);
      } else {
        const allMessages = [];
        if (Array.isArray(serverErrors)) allMessages.push(...serverErrors);
        if (user !== undefined) allMessages.push(getStatusMessage(user));
        if (otp !== undefined) allMessages.push(getStatusMessage(otp));
        setErrors(allMessages.length ? allMessages : ['Signup failed.']);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      setTimeout(() => setAnimateArrow(false), 800);

      if (status === 400 && Array.isArray(data?.errors)) {
        setErrors(data.errors);
      } else if (status === 400 && (data?.user || data?.otp)) {
        const failMessages = [];
        if (typeof data.user === 'number') failMessages.push(getStatusMessage(data.user));
        if (typeof data.otp === 'number') failMessages.push(getStatusMessage(data.otp));
        setErrors(failMessages);
      } else {
        setErrors(['Server error. Please try again later.']);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
<div>

    <div className="animated-bg d-flex flex-column min-vh-100 p-4" style={{ background: 'linear-gradient(135deg, #ffe0f0, #d0e8ff)' }}>
      {/* SEO Meta Tags */}
 


 {/* SEO */}
      <Helmet>
        <title>Signup | LuvMarry Australia – Dating Website for Singles</title>
        <meta
          name="description"
          content="Join LuvMarry Australia – Meet singles across Sydney, Melbourne, Perth, Brisbane & Adelaide. Free signup & safe online dating platform."
        />
        <meta
          name="keywords"
          content="dating site Australia, Australian singles, online dating Australia, meet singles Australia, Sydney dating, Melbourne dating"
        />
        <link rel="canonical" href="https://aus.luvmarry.com/signup" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="LuvMarry Australia Dating" />
        <meta
          property="og:description"
          content="Create your free account and meet real singles across Australia."
        />
        <meta property="og:image" content="/LuvMarryLogoPink.png" />
        <meta property="og:url" content="https://aus.luvmarry.com/signup" />
        <meta property="og:type" content="website" />

        {/* Geo */}
        <meta name="geo.region" content="AU" />
        <meta name="geo.placename" content="Australia" />
        <meta name="language" content="en-AU" />

        {/* Schema */}
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "DatingService",
          "name": "LuvMarry Australia",
          "url": "https://aus.luvmarry.com",
          "logo": "https://aus.luvmarry.com/LuvMarryLogoPink.png",
          "areaServed": { "@type": "Country", "name": "Australia" }
        }
        `}</script>
      </Helmet>



      {/* Floating background symbols */}
      {Array.from({ length: 30 }).map((_, i) => {
        const symbol = symbols[Math.floor(Math.random() * symbols.length)];
        return (
          <span
            key={i}
            className="position-absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${12 + Math.random() * 14}px`,
              opacity: 0.04,
              animation: 'floatDown 20s linear infinite',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            {symbol}
          </span>
        );
      })}

      {/* Signup Form */}
      <div className="container" style={{ maxWidth: 500, zIndex: 2 }}>
        <div className="text-center mb-4">
          <span className="badge bg-secondary">Step 1 of 4</span>
          <h6 className="mt-2 wedding-heading">Join LuvMarry Australia </h6>
          <p className="text-muted">Meet Singles Across Australia 💖 💗</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label htmlFor="email" className="form-label text-muted">Email address</label>
            <input
              id="email"
              type="email"
              className="form-control"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label text-muted">Password</label>
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder="8–12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="termsCheck"
              checked={acceptedTerms}
              onChange={(e) => {
                if (!acceptedTerms) {
                  setShowTermsPopup(true);
                  setScrolledToBottom(false);
                } else {
                  setAcceptedTerms(false);
                }
              }}
            />
            <label className="form-check-label" htmlFor="termsCheck">
              I agree to the{' '}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => {
                  setShowTermsPopup(true);
                  setScrolledToBottom(false);
                }}
              >
                Terms & Conditions
              </button>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn w-100 d-flex justify-content-between align-items-center mt-2"
            style={{ backgroundColor: '#ff69b4', color: 'white' }}
            disabled={loading || !acceptedTerms}
          >
            <span>{loading ? 'Creating account...' : 'Continue to OTP'}</span>
            <i
              className={`bi bi-arrow-right-circle-fill ${animateArrow ? 'move-arrow' : ''}`}
              style={{ fontSize: '1.4rem' }}
            ></i>
          </button>
        </form>

        {/* Messages */}
        {message && (
          <div className="alert alert-success mt-3" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </div>
        )}

        {errors.length > 0 && (
          <div className="alert alert-danger mt-3">
            <ul className="mb-0">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}

        {/* Login Link */}
        <div className="text-center mt-4">
          <small className="text-muted">
            Already have an account?{' '}
            <a href="/login" className="text-primary">Log in</a>
          </small>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsPopup && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
          onClick={() => setShowTermsPopup(false)}
        >
          <div
            className="modal-dialog modal-dialog-scrollable modal-lg modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header bg-light border-0" style={{ backgroundColor: '#fff0f6' }}>
                <h2 className="modal-title text-pink fw-bold fs-5">
                  <i className="bi bi-bookmark-heart-fill me-2"></i>Terms & Conditions
                </h2>
                <button type="button" className="btn-close" onClick={() => setShowTermsPopup(false)} aria-label="Close"></button>
              </div>

              <div
                className="modal-body text-muted"
                style={{ maxHeight: '60vh', overflowY: 'auto' }}
                onScroll={(e) => {
                  const el = e.target;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                    setScrolledToBottom(true);
                  }
                }}
              >
                {/* List items remain same */}
                <div className="list-group">
                  <div className="list-group-item"><i className="bi bi-person-check-fill text-success me-2"></i>You must be 18+ years old.</div>
                  <div className="list-group-item"><i className="bi bi-heart-fill text-danger me-2"></i>Respect other users.</div>
                  <div className="list-group-item"><i className="bi bi-person-x-fill text-warning me-2"></i>No fake profiles.</div>
                  <div className="list-group-item"><i className="bi bi-lock-fill text-dark me-2"></i>Never share sensitive information.</div>
                  <div className="list-group-item"><i className="bi bi-shield-x text-danger me-2"></i>No scamming or explicit content.</div>
                </div>
                <div className="mt-3 text-center">❤️ Be honest and kind. Find meaningful connections.</div>
              </div>

              <div className="modal-footer bg-light border-0 d-flex flex-column flex-md-row gap-2">
                <button
                  className="btn btn-outline-secondary w-100 rounded-pill"
                  onClick={() => {
                    setShowTermsPopup(false);
                    setScrolledToBottom(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn w-100 rounded-pill"
                  disabled={!scrolledToBottom}
                  style={{ backgroundColor: '#ff69b4', borderColor: '#ff69b4' }}
                  onClick={() => {
                    setAcceptedTerms(true);
                    setShowTermsPopup(false);
                  }}
                >
                  I Agree 💖
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>



      {/* SEO BODY CONTENT */}
      <section className="seo-content">
        <h2>Australia Dating Site for Real Connections</h2>

        <p>
          LuvMarry Australia is a trusted dating platform helping singles meet
          across Sydney, Melbourne, Brisbane, Perth, and Adelaide.
        </p>

        <h3>Why Singles Choose LuvMarry</h3>
        <ul>
          <li>✔ 100% Free Registration</li>
          <li>✔ Secure & Private Profiles</li>
          <li>✔ Real Australian Singles</li>
          <li>✔ Designed for Dating & Relationships</li>
        </ul>

        <h3>Popular Locations</h3>
        <p>
          <a href="/search?city=sydney">Sydney</a> ·
          <a href="/search?city=melbourne"> Melbourne</a> ·
          <a href="/search?city=brisbane"> Brisbane</a> ·
          <a href="/search?city=perth"> Perth</a> ·
          <a href="/search?city=adelaide"> Adelaide</a>
        </p>
      </section>


</div>
    
    
  );
}

export default Signup;
